import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type PaceSplit = {
  km: number;
  target_pace_sec: number;
  cumulative_time_sec: number;
  note: string;
};

export type NutritionStop = {
  minute: number;
  item: "water" | "gel" | "salt" | "electrolyte" | string;
  note: string;
};

export type RaceDayPlan = {
  target_finish_seconds: number | null;
  target_pace_sec: number | null;
  splits_5k: PaceSplit[];
  nutrition: NutritionStop[];
  tracker_apps: string[];
  notes: string[];
  bib_number: string | null;
  tracker_link: string | null;
};

export type ChecklistItem = {
  key: string;
  title: string;
  hint: string;
  icon: string;
  critical: boolean;
  checked: boolean;
};

export type ChecklistStage = {
  key: "T_30" | "T_14" | "T_7" | "T_3" | "T_1" | "race_day";
  title: string;
  days_until_at_stage: number;
  items: ChecklistItem[];
  completed_count: number;
  total_count: number;
  is_current: boolean;
};

export type RaceWeekOut = {
  active: boolean;
  days_until_race: number | null;
  race_date: string | null;
  race_name: string | null;
  race_city: string | null;
  race_country: string | null;
  distance_km: number | null;
  distance_bucket: string | null;
  discipline: string | null;
  current_stage_key: string | null;
  stages: ChecklistStage[];
  note: string;
  race_day_plan: RaceDayPlan | null;
  finisher_goal_id: number | null;
};

export type ShareCardOut = { url: string; fmt: string; ref_token: string };

export function useGenerateShareCard() {
  return useMutation({
    mutationFn: async (vars: {
      goalId: number;
      fmt?: "story" | "square";
      photo?: File | null;
    }) => {
      const fmt = vars.fmt ?? "story";
      if (vars.photo) {
        const form = new FormData();
        form.append("photo", vars.photo);
        const r = await api.post<ShareCardOut>(
          `/athlete/race/${vars.goalId}/share-card/with-photo?fmt=${fmt}`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        return r.data;
      }
      const r = await api.post<ShareCardOut>(
        `/athlete/race/${vars.goalId}/share-card?fmt=${fmt}`,
      );
      return r.data;
    },
  });
}

export function useRaceWeek() {
  return useQuery<RaceWeekOut>({
    queryKey: ["athlete-race-week"],
    queryFn: async () => {
      const r = await api.get<RaceWeekOut>("/athlete/race-week");
      return r.data;
    },
    staleTime: 60_000,
    retry: false,
  });
}

export type TravelAdvice = {
  available: boolean;
  no_travel_needed?: boolean;
  race_utc_offset_hours?: number | null;
  athlete_utc_offset_hours?: number | null;
  tz_diff_hours?: number | null;
  travel_direction?: "east" | "west" | null;
  recommended_arrival_days_before?: number | null;
  arrival_advice?: string | null;
  jet_lag_protocol?: string[];
  note?: string | null;
  recommended_wake_time?: string | null;   // "HH:MM"
  recommended_sleep_time?: string | null;  // "HH:MM"
  race_start_time?: string | null;         // "HH:MM"
};

// ── Live race cockpit (PHASE 8 R9) ────────────────────────

export type RaceLiveState = "none" | "pre" | "morning" | "start" | "work" | "finish";

export type RaceLiveOut = {
  has_race: boolean;
  state: RaceLiveState;
  race_id?: number | null;
  race_name?: string | null;
  race_date?: string | null;
  race_start_time?: string | null;
  distance_km?: number | null;
  discipline?: string | null;
  target_time_seconds?: number | null;
  target_pace_sec_per_km?: number | null;
  days_until?: number | null;
  seconds_until_start?: number | null;
  seconds_since_start?: number | null;
  countdown_label?: string | null;
  current_pace_sec_per_km?: number | null;
  current_hr?: number | null;
  coach_message?: string | null;
  covered_km?: number | null;
  remaining_km?: number | null;
  estimated_finish_seconds?: number | null;
  finish_delta_seconds?: number | null;
};

export function useRaceLive() {
  return useQuery<RaceLiveOut>({
    queryKey: ["athlete-race-live"],
    queryFn: async () => {
      const r = await api.get<RaceLiveOut>("/athlete/race/live");
      return r.data;
    },
    // Frequent polls only when we're actually in the cockpit; staleTime
    // keeps it light when component remounts.
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: false,
  });
}

export function useLogTelemetry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      pace_sec_per_km?: number;
      hr_bpm?: number;
      distance_km?: number;
    }) => {
      const r = await api.post<{
        id: number;
        goal_id: number;
        recorded_at: string;
        pace_sec_per_km: number | null;
        hr_bpm: number | null;
        distance_km: number | null;
      }>("/athlete/race/telemetry", vars);
      return r.data;
    },
    onSuccess: () => {
      // Refresh LiveRacePage cockpit immediately.
      qc.invalidateQueries({ queryKey: ["athlete-race-live"] });
    },
  });
}

export function useTravelAdvice() {
  return useQuery<TravelAdvice>({
    queryKey: ["athlete-race-week-travel"],
    queryFn: async () => {
      const r = await api.get<TravelAdvice>("/athlete/race-week/travel-advice");
      return r.data;
    },
    staleTime: 60 * 60 * 1000, // travel advice rarely changes
    retry: false,
  });
}

export function useUpdateRaceDayFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { bib_number?: string; tracker_link?: string }) => {
      const r = await api.patch<{ bib_number: string | null; tracker_link: string | null }>(
        "/athlete/race-week/race-day", vars,
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete-race-week"] }),
  });
}

export function useShareToCoach() {
  return useMutation({
    mutationFn: async (vars: { message?: string }) => {
      const r = await api.post<{ ok: boolean }>(
        "/athlete/race-week/share-to-coach", vars,
      );
      return r.data;
    },
  });
}


export function useToggleRaceWeekItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemKey: string; checked: boolean }) => {
      const r = await api.patch<ChecklistItem>(
        `/athlete/race-week/items/${vars.itemKey}`,
        { checked: vars.checked },
      );
      return r.data;
    },
    onMutate: async (vars) => {
      // Optimistic update — мгновенно перерисуем чек.
      await qc.cancelQueries({ queryKey: ["athlete-race-week"] });
      const prev = qc.getQueryData<RaceWeekOut>(["athlete-race-week"]);
      if (prev) {
        qc.setQueryData<RaceWeekOut>(["athlete-race-week"], {
          ...prev,
          stages: prev.stages.map((s) => ({
            ...s,
            items: s.items.map((it) =>
              it.key === vars.itemKey ? { ...it, checked: vars.checked } : it,
            ),
            completed_count:
              s.items.reduce(
                (sum, it) =>
                  sum +
                  (it.key === vars.itemKey
                    ? vars.checked
                      ? 1
                      : 0
                    : it.checked
                      ? 1
                      : 0),
                0,
              ),
          })),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["athlete-race-week"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["athlete-race-week"] });
    },
  });
}
